import "../css/header.scss";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiChevronDown,
  FiHeart,
  FiMenu,
  FiMinus,
  FiPlus,
  FiSearch,
  FiShoppingBag,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import siteConfig, { formatPrice } from "../config/siteConfig";
import productData from "../Datas/productData";
import { CartContext } from "./CartContext";

const womenMenuGroups = [
  {
    title: "Highlights",
    links: [
      ["New arrivals", "/shop?collection=new"],
      ["All items", "/shop?collection=women"],
      ["Travel collection", "/shop?tag=travel"],
      ["Co-ord sets", "/shop?tag=co-ord"],
    ],
  },
  {
    title: "Clothing",
    links: [
      ["Sweaters", "/shop?category=Sweaters"],
      ["Pants", "/shop?category=Pants"],
      ["Shirts", "/shop?category=Shirts"],
      ["Dresses", "/shop?category=Dresses"],
      ["Jackets", "/shop?category=Jackets"],
    ],
  },
  {
    title: "Accessories",
    links: [
      ["Bags", "/shop?category=Bags"],
      ["Scarves", "/shop?category=Scarves"],
      ["Socks", "/shop?category=Underwear%20%26%20socks"],
      ["View all", "/shop?tag=accessories"],
    ],
  },
];

const mobileMenuGroups = [
  {
    title: "Women",
    links: [
      ["All women", "/shop?collection=women"],
      ["Sweaters", "/shop?category=Sweaters"],
      ["Pants", "/shop?category=Pants"],
      ["Shirts & blouses", "/shop?category=Shirts"],
      ["Dresses", "/shop?category=Dresses"],
      ["Accessories", "/shop?tag=accessories"],
    ],
  },
  {
    title: "Men",
    links: [
      ["All men", "/shop?collection=men"],
      ["Pants", "/shop?collection=men&category=Pants"],
      ["T-shirts", "/shop?collection=men&category=T-shirts"],
      ["Travel essentials", "/shop?collection=men&tag=travel"],
    ],
  },
  {
    title: "Homeware",
    links: [
      ["All homeware", "/shop?collection=homeware"],
      ["Home fragrance", "/shop?collection=homeware&tag=fragrance"],
      ["Pillow covers", "/shop?collection=homeware&category=Pillow%20covers"],
    ],
  },
];

const moreLinks = [
  ["FAQs", "/faqs"],
  ["Privacy", "/privacy"],
  ["Cookie policy", "/cookies"],
  ["Terms", "/terms"],
  ["Delivery", "/shipping"],
  ["Returns", "/returns"],
  ["Size guide", "/size-guide"],
  ["Payment & security", "/payment-security"],
  ["Materials & care", "/materials-care"],
  ["Accessibility", "/accessibility"],
];

function BrandMark({ label }) {
  return (
    <span className="brand-mark" aria-hidden="true">
      <span className="brand-mark__monogram">TL</span>
      <span className="brand-mark__name">{label}</span>
    </span>
  );
}

function Header() {
  const cart = useContext(CartContext) || {};
  const {
    cartItems = [],
    wishlistItems: contextWishlistItems,
    wishlist,
    removeFromWishlist,
    removeWishlist,
    removeFromCart,
    updateQuantity,
    isCartOpen = false,
    openCart,
    closeCart,
    toggleCart,
  } = cart;
  const wishlistItems = Array.isArray(contextWishlistItems)
    ? contextWishlistItems
    : Array.isArray(wishlist)
    ? wishlist
    : [];
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isWishlistOpen, setIsWishlistOpen] = useState(false);
  const [isWomenOpen, setIsWomenOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeAnnouncement, setActiveAnnouncement] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const brandLabel =
    siteConfig.logo?.text || siteConfig.logoText || siteConfig.brandName;
  const evergreenLabel = siteConfig.collections.coreLabel;
  const freeShippingThreshold =
    siteConfig.shipping?.freeThreshold ?? siteConfig.freeShipping?.threshold;
  const returnDays =
    siteConfig.shipping?.returnDays ?? siteConfig.returns?.windowDays;
  const announcementItems = [
    freeShippingThreshold
      ? `Free delivery over ${formatPrice(freeShippingThreshold)}`
      : "Free delivery on qualifying orders",
    returnDays ? `${returnDays}-day returns` : "Easy returns",
    siteConfig.tagline,
  ].filter(Boolean);

  const cartCount = cartItems.reduce(
    (total, item) => total + (Number(item.quantity) || 0),
    0
  );
  const cartLines = cartItems
    .map((item) => ({
      ...item,
      product: productData.find((product) => product.id === Number(item.id)),
    }))
    .filter((item) => item.product);
  const cartSubtotal = cartLines.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );
  const cartShipping =
    cartSubtotal >= Number(freeShippingThreshold || 0)
      ? 0
      : Number(siteConfig.shipping?.flatRate || 0);
  const cartTotal = cartSubtotal + cartShipping;

  const wishlistProducts = wishlistItems
    .map((item) => {
      const itemId = typeof item === "object" ? item.id : item;
      if (typeof item === "object" && item.name) return item;
      return productData.find((product) => product.id === Number(itemId));
    })
    .filter(Boolean);

  const closeAllPanels = () => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
    setIsWishlistOpen(false);
    setIsWomenOpen(false);
    setIsMoreOpen(false);
    closeCart?.();
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mediaQuery) return undefined;

    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);
    updatePreference();
    mediaQuery.addEventListener?.("change", updatePreference);
    return () => mediaQuery.removeEventListener?.("change", updatePreference);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion || announcementItems.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setActiveAnnouncement(
        (current) => (current + 1) % announcementItems.length
      );
    }, 5000);
    return () => window.clearInterval(timer);
  }, [announcementItems.length, prefersReducedMotion]);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
    setIsWishlistOpen(false);
    setIsWomenOpen(false);
    setIsMoreOpen(false);
    closeCart?.();
  }, [closeCart, location.pathname, location.search]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
        setIsSearchOpen(false);
        setIsWishlistOpen(false);
        setIsWomenOpen(false);
        setIsMoreOpen(false);
        closeCart?.();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [closeCart]);

  useEffect(() => {
    const shouldLock = isMenuOpen || isWishlistOpen || isCartOpen;
    const previousOverflow = document.body.style.overflow;
    if (shouldLock) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isCartOpen, isMenuOpen, isWishlistOpen]);

  useEffect(() => {
    if (isSearchOpen) searchInputRef.current?.focus();
  }, [isSearchOpen]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const query = searchTerm.trim();
    navigate(query ? `/shop?search=${encodeURIComponent(query)}` : "/shop");
    scrollToTop();
  };

  const openMenu = () => {
    setIsWishlistOpen(false);
    setIsSearchOpen(false);
    setIsWomenOpen(false);
    setIsMoreOpen(false);
    closeCart?.();
    setIsMenuOpen(true);
  };

  const openWishlist = () => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
    setIsWomenOpen(false);
    setIsMoreOpen(false);
    closeCart?.();
    setIsWishlistOpen(true);
  };

  const handleCartToggle = () => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
    setIsWishlistOpen(false);
    setIsMoreOpen(false);
    setIsWomenOpen(false);
    if (typeof toggleCart === "function") toggleCart();
    else if (isCartOpen) closeCart?.();
    else openCart?.();
  };

  const openWomenMenu = () => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
    setIsWishlistOpen(false);
    closeCart?.();
    setIsMoreOpen(false);
    setIsWomenOpen(true);
  };

  const handleRemoveWishlist = (productId) => {
    const removeItem = removeFromWishlist || removeWishlist;
    if (typeof removeItem === "function") removeItem(productId);
  };

  const handleQuantityChange = (item, nextQuantity) => {
    if (typeof updateQuantity !== "function") return;
    updateQuantity(item.id, item.size, item.color, nextQuantity);
  };

  const handleRemoveCartItem = (item) => {
    if (typeof removeFromCart !== "function") return;
    removeFromCart(item.id, item.size, item.color);
  };

  const desktopLinks = [
    ["SHOP ALL", "/shop"],
    ["Men", "/shop?collection=men"],
    [evergreenLabel, "/shop?collection=365"],
    ["Homeware", "/shop?collection=homeware"],
  ];

  return (
    <header className="site-header">
      <div className="announcement-bar" aria-label="Store benefits">
        <div className="announcement-bar__inner" aria-live="off">
          <span
            className="announcement-bar__item"
            key={`${activeAnnouncement}-${announcementItems[activeAnnouncement]}`}
          >
            {announcementItems[activeAnnouncement]}
          </span>
          <span className="announcement-bar__dots" aria-hidden="true">
            {announcementItems.map((item, index) => (
              <i
                className={index === activeAnnouncement ? "is-active" : ""}
                key={item}
              />
            ))}
          </span>
        </div>
      </div>

      <div className="site-header__main">
        <button
          className="site-header__menu-button"
          type="button"
          aria-label="Open menu"
          aria-controls="mobile-navigation"
          aria-expanded={isMenuOpen}
          onClick={openMenu}
        >
          <FiMenu aria-hidden="true" />
        </button>

        <nav className="site-header__nav" aria-label="Primary navigation">
          <Link to={desktopLinks[0][1]} onClick={scrollToTop}>
            {desktopLinks[0][0]}
          </Link>
          <div
            className="site-header__nav-item site-header__nav-item--mega"
            onMouseEnter={openWomenMenu}
            onMouseLeave={() => setIsWomenOpen(false)}
            onFocusCapture={openWomenMenu}
            onBlurCapture={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setIsWomenOpen(false);
              }
            }}
          >
            <Link
              to="/shop?collection=women"
              aria-controls="women-mega-menu"
              aria-expanded={isWomenOpen}
              onClick={scrollToTop}
            >
              Women
            </Link>
            <aside
              id="women-mega-menu"
              className={`women-mega ${isWomenOpen ? "is-open" : ""}`}
              aria-hidden={!isWomenOpen}
              aria-label="Women collections"
            >
              <div className="women-mega__links">
                {womenMenuGroups.map((group) => (
                  <section key={group.title}>
                    <h2>{group.title}</h2>
                    {group.links.map(([label, to]) => (
                      <Link key={label} to={to} onClick={scrollToTop}>
                        {label}
                      </Link>
                    ))}
                  </section>
                ))}
              </div>
              <Link
                className="women-mega__editorial"
                to="/shop?tag=summer"
                onClick={scrollToTop}
              >
                <img
                  src="/img/editorial/header-summer-editorial.jpg"
                  alt="Relaxed summer tailoring"
                />
                <span>
                  <small>Editorial</small>
                  Sunlit Days
                </span>
              </Link>
            </aside>
          </div>
          {desktopLinks.slice(1).map(([label, to]) => (
            <Link key={label} to={to} onClick={scrollToTop}>
              {label}
            </Link>
          ))}
          <div className="site-header__nav-item site-header__nav-item--more">
            <button
              type="button"
              aria-expanded={isMoreOpen}
              aria-controls="header-more-menu"
              onClick={() => setIsMoreOpen((open) => !open)}
            >
              More <FiChevronDown aria-hidden="true" />
            </button>
            <div
              id="header-more-menu"
              className={`header-more-menu ${isMoreOpen ? "is-open" : ""}`}
              hidden={!isMoreOpen}
            >
              {moreLinks.map(([label, to]) => (
                <Link key={label} to={to} onClick={scrollToTop}>
                  {label}
                </Link>
              ))}
            </div>
          </div>
        </nav>

        <Link
          className="site-header__logo"
          to="/"
          aria-label={`${siteConfig.brandName} home`}
          onClick={scrollToTop}
        >
          <BrandMark label={brandLabel} />
        </Link>

        <div className="site-header__utilities">
          <form
            className={`header-search ${isSearchOpen ? "is-open" : ""}`}
            role="search"
            onSubmit={handleSearchSubmit}
          >
            <label className="sr-only" htmlFor="header-search-input">
              Search products
            </label>
            {isSearchOpen && (
              <input
                ref={searchInputRef}
                id="header-search-input"
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search"
                autoComplete="off"
              />
            )}
            <button
              type={isSearchOpen ? "submit" : "button"}
              aria-label={isSearchOpen ? "Submit search" : "Open search"}
              aria-expanded={isSearchOpen}
              onClick={() => {
                if (!isSearchOpen) {
                  setIsMenuOpen(false);
                  setIsWishlistOpen(false);
                  setIsWomenOpen(false);
                  closeCart?.();
                  setIsSearchOpen(true);
                }
              }}
            >
              <FiSearch aria-hidden="true" />
            </button>
            {isSearchOpen && (
              <button
                className="header-search__close"
                type="button"
                aria-label="Close search"
                onClick={() => setIsSearchOpen(false)}
              >
                <FiX aria-hidden="true" />
              </button>
            )}
          </form>

          <button
            className="header-icon-button header-icon-button--wishlist"
            type="button"
            aria-label={`Open wishlist with ${wishlistItems.length} items`}
            aria-controls="wishlist-panel"
            aria-expanded={isWishlistOpen}
            onClick={openWishlist}
          >
            <FiHeart aria-hidden="true" />
            {wishlistItems.length > 0 && (
              <span className="header-count" aria-hidden="true">
                {wishlistItems.length}
              </span>
            )}
          </button>

          <button
            className="header-icon-button"
            type="button"
            aria-label={`Shopping bag with ${cartCount} items`}
            aria-controls="cart-panel"
            aria-expanded={isCartOpen}
            onClick={handleCartToggle}
          >
            <FiShoppingBag aria-hidden="true" />
            {cartCount > 0 && (
              <span className="header-count" aria-hidden="true">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {isWomenOpen && (
        <button
          className="mega-menu-backdrop"
          type="button"
          aria-label="Close women menu"
          onClick={() => setIsWomenOpen(false)}
        />
      )}

      {(isMenuOpen || isWishlistOpen || isCartOpen) && (
        <button
          className="header-overlay"
          type="button"
          aria-label="Close open panel"
          onClick={closeAllPanels}
        />
      )}

      <aside
        id="mobile-navigation"
        className={`mobile-menu ${isMenuOpen ? "is-open" : ""}`}
        aria-hidden={!isMenuOpen}
        aria-label="Mobile navigation"
      >
        <div className="mobile-menu__header">
          <span className="mobile-menu__logo">
            <BrandMark label={brandLabel} />
          </span>
          <button
            type="button"
            aria-label="Close menu"
            onClick={closeAllPanels}
          >
            <FiX aria-hidden="true" />
          </button>
        </div>
        <form
          className="mobile-menu__search"
          role="search"
          onSubmit={handleSearchSubmit}
        >
          <label className="sr-only" htmlFor="mobile-search-input">
            Search products
          </label>
          <input
            id="mobile-search-input"
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search products"
          />
          <button type="submit" aria-label="Submit search">
            <FiSearch aria-hidden="true" />
          </button>
        </form>
        <nav
          className="mobile-menu__links"
          aria-label="Mobile primary navigation"
        >
          <Link
            className="mobile-menu__primary-link"
            to="/shop?collection=new"
            onClick={scrollToTop}
          >
            New <span aria-hidden="true">↗</span>
          </Link>
          {mobileMenuGroups.slice(0, 2).map((group, index) => (
            <details key={group.title} open={index === 0}>
              <summary>
                {group.title} <FiChevronDown aria-hidden="true" />
              </summary>
              <div>
                {group.links.map(([label, to]) => (
                  <Link key={label} to={to} onClick={scrollToTop}>
                    {label}
                  </Link>
                ))}
              </div>
            </details>
          ))}
          <Link
            className="mobile-menu__primary-link"
            to="/shop?collection=365"
            onClick={scrollToTop}
          >
            {evergreenLabel} <span aria-hidden="true">↗</span>
          </Link>
          <details>
            <summary>
              {mobileMenuGroups[2].title} <FiChevronDown aria-hidden="true" />
            </summary>
            <div>
              {mobileMenuGroups[2].links.map(([label, to]) => (
                <Link key={label} to={to} onClick={scrollToTop}>
                  {label}
                </Link>
              ))}
            </div>
          </details>
          <details>
            <summary>
              More <FiChevronDown aria-hidden="true" />
            </summary>
            <div>
              {moreLinks.map(([label, to]) => (
                <Link key={label} to={to} onClick={scrollToTop}>
                  {label}
                </Link>
              ))}
            </div>
          </details>
        </nav>
        <div className="mobile-menu__footer">
          <p>{siteConfig.tagline}</p>
          {siteConfig.contact?.email && (
            <a href={`mailto:${siteConfig.contact.email}`}>
              {siteConfig.contact.email}
            </a>
          )}
        </div>
      </aside>

      <aside
        id="wishlist-panel"
        className={`wishlist-panel ${isWishlistOpen ? "is-open" : ""}`}
        role="dialog"
        aria-modal={isWishlistOpen || undefined}
        aria-hidden={!isWishlistOpen}
        aria-labelledby="wishlist-panel-title"
      >
        <div className="wishlist-panel__header">
          <h2 id="wishlist-panel-title">Wishlist ({wishlistItems.length})</h2>
          <button
            type="button"
            aria-label="Close wishlist"
            onClick={closeAllPanels}
          >
            <FiX aria-hidden="true" />
          </button>
        </div>
        <div className="wishlist-panel__content">
          {wishlistProducts.length === 0 ? (
            <div className="wishlist-panel__empty">
              <FiHeart aria-hidden="true" />
              <p>Your wishlist is empty.</p>
              <Link to="/shop" onClick={scrollToTop}>
                Discover the collection
              </Link>
            </div>
          ) : (
            <ul className="wishlist-panel__list">
              {wishlistProducts.map((product) => (
                <li key={product.id}>
                  <Link
                    className="wishlist-panel__product"
                    to={`/shopDetails/${product.id}`}
                    onClick={scrollToTop}
                  >
                    <img
                      src={product.image || product.images?.[0]}
                      alt=""
                      loading="lazy"
                    />
                    <span>
                      <strong>{product.name}</strong>
                      <small>{formatPrice(product.price)}</small>
                    </span>
                  </Link>
                  {(removeFromWishlist || removeWishlist) && (
                    <button
                      className="wishlist-panel__remove"
                      type="button"
                      aria-label={`Remove ${product.name} from wishlist`}
                      onClick={() => handleRemoveWishlist(product.id)}
                    >
                      <FiTrash2 aria-hidden="true" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>

      <aside
        id="cart-panel"
        className={`cart-panel ${isCartOpen ? "is-open" : ""}`}
        role="dialog"
        aria-modal={isCartOpen || undefined}
        aria-hidden={!isCartOpen}
        aria-labelledby="cart-panel-title"
      >
        <div className="cart-panel__header">
          <h2 id="cart-panel-title">Shopping bag ({cartCount})</h2>
          <button
            type="button"
            aria-label="Close shopping bag"
            onClick={closeCart}
          >
            <FiX aria-hidden="true" />
          </button>
        </div>

        {cartLines.length === 0 ? (
          <div className="cart-panel__empty">
            <FiShoppingBag aria-hidden="true" />
            <p>Your shopping bag is empty.</p>
            <Link to="/shop" onClick={scrollToTop}>
              Discover the collection
            </Link>
          </div>
        ) : (
          <>
            <div className="cart-panel__content">
              <ul className="cart-panel__list">
                {cartLines.map((item) => (
                  <li key={`${item.id}-${item.size}-${item.color}`}>
                    <Link
                      className="cart-panel__image"
                      to={`/shopDetails/${item.product.id}`}
                      onClick={scrollToTop}
                    >
                      <img src={item.product.image} alt={item.product.name} />
                    </Link>
                    <div className="cart-panel__details">
                      <Link
                        to={`/shopDetails/${item.product.id}`}
                        onClick={scrollToTop}
                      >
                        {item.product.name}
                      </Link>
                      <p>
                        {item.color} · {item.size}
                      </p>
                      <strong>
                        {formatPrice(item.product.price * item.quantity)}
                      </strong>
                      <div className="cart-panel__item-actions">
                        <div
                          className="cart-panel__quantity"
                          aria-label={`Quantity for ${item.product.name}`}
                        >
                          <button
                            type="button"
                            aria-label="Decrease quantity"
                            disabled={item.quantity <= 1}
                            onClick={() =>
                              handleQuantityChange(item, item.quantity - 1)
                            }
                          >
                            <FiMinus aria-hidden="true" />
                          </button>
                          <span>{item.quantity}</span>
                          <button
                            type="button"
                            aria-label="Increase quantity"
                            disabled={item.quantity >= 99}
                            onClick={() =>
                              handleQuantityChange(item, item.quantity + 1)
                            }
                          >
                            <FiPlus aria-hidden="true" />
                          </button>
                        </div>
                        <button
                          className="cart-panel__remove"
                          type="button"
                          onClick={() => handleRemoveCartItem(item)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="cart-panel__summary">
              <p>
                <span>Subtotal</span>
                <strong>{formatPrice(cartSubtotal)}</strong>
              </p>
              <p>
                <span>Delivery</span>
                <strong>
                  {cartShipping === 0 ? "Free" : formatPrice(cartShipping)}
                </strong>
              </p>
              <p className="cart-panel__total">
                <span>Total</span>
                <strong>{formatPrice(cartTotal)}</strong>
              </p>
              <Link
                className="cart-panel__checkout"
                to="/checkout"
                onClick={closeCart}
              >
                Check out · {formatPrice(cartTotal)}
              </Link>
              <Link
                className="cart-panel__view-bag"
                to="/cartpage"
                onClick={closeCart}
              >
                View shopping bag
              </Link>
            </div>
          </>
        )}
      </aside>
    </header>
  );
}

export default Header;
